import { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Truck, Package, Coins, Weight, Box, DollarSign, Link2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils/cn';
import { useToast } from '@/lib/hooks/use-toast';
import { importTrackingService } from '../services/import-tracking-service';
import { freightServicesService } from '../services/freight-services-service';
import {
  freightProviderService,
  type FreightProvider,
} from '@/features/master-data/services/freight-provider-service';
import {
  freightLegPricingsService,
  type FreightRouteLegProviderPricing,
} from '@/features/master-data/services/freight-leg-pricings-service';
import { freightLegCatalogService } from '@/features/master-data/services/freight-leg-catalog-service';
import {
  type ImportOrderLeg,
  type ImportOrderLegService,
  type FreightService,
  type FreightServiceProviderPricing,
  type FreightPricingType,
  FREIGHT_PRICING_LABEL,
} from '../types/import-tracking.types';

interface LegPricingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leg: ImportOrderLeg;
  onSaved: () => void;
}

const PRICING_TYPES: FreightPricingType[] = ['WEIGHT', 'VOLUME', 'FLAT'];

const KIND_LABEL: Record<FreightPricingType, string> = {
  WEIGHT: 'Por peso',
  VOLUME: 'Por volumen',
  FLAT: 'Monto fijo',
};

const KIND_UNIT: Record<FreightPricingType, string> = {
  WEIGHT: '/kg',
  VOLUME: '/m³',
  FLAT: '',
};

function asNumber(v: number | string | null | undefined): number {
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(v: number | string | null | undefined, currency = 'USD'): string {
  const n = asNumber(v);
  return `${currency} ${n.toLocaleString('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function LegPricingDialog({ open, onOpenChange, leg, onSaved }: LegPricingDialogProps) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  // providers ya no se usa: el proveedor del tramo viene del leg, fijado al
  // crearlo desde el catálogo. No se cambia desde este dialog.
  // (mantenemos el setter como void para no romper el destructuring del Promise.all)
  const [, setProviders] = useState<FreightProvider[]>([]);
  const [services, setServices] = useState<FreightService[]>([]);
  const [servicePricings, setServicePricings] = useState<FreightServiceProviderPricing[]>([]);
  const [legServices, setLegServices] = useState<ImportOrderLegService[]>([]);
  // Pricing del catálogo para (legCatalogId, providerId actual). Si existe,
  // mostramos sus tarifas como atajos para llenar el form.
  const [catalogPricing, setCatalogPricing] = useState<FreightRouteLegProviderPricing | null>(null);
  // allCatalogPricings ya no se usa porque el dropdown de proveedor fue
  // removido. Se mantiene el setter en el Promise.all sin lectura del state.
  const [, setAllCatalogPricings] = useState<FreightRouteLegProviderPricing[]>([]);
  // Sugerencia de vínculo cuando el leg quedó huérfano (legCatalogId = null).
  // Si encontramos exactamente 1 tramo del catálogo que matchea por
  // (origin, destination, transportMode, legType), la usamos para mostrar
  // tarifas y persistimos el vínculo al guardar.
  const [suggestedVinculo, setSuggestedVinculo] = useState<{
    legId: string;
    providerId: string | null;
    providerName: string | null;
  } | null>(null);
  const [ambiguousCatalogCount, setAmbiguousCatalogCount] = useState(0);

  // Local form state — only the editable pricing/cargo fields
  const [form, setForm] = useState({
    cargoWeightKg: asNumber(leg.cargoWeightKg),
    cargoVolumeCbm: asNumber(leg.cargoVolumeCbm),
    providerId: leg.providerId ?? '',
    pricingType: (leg.pricingType ?? 'FLAT') as FreightPricingType,
    unitPrice: asNumber(leg.unitPrice),
    minimumCharge: asNumber(leg.minimumCharge),
    currency: leg.currency || 'USD',
  });

  // ── Loaders ──────────────────────────────────────────────────────
  const loadCatalogs = useCallback(async () => {
    try {
      const [providersData, servicesData, pricingsData, legPricingsData] = await Promise.all([
        freightProviderService.getAll().catch(() => []),
        freightServicesService.list().catch(() => []),
        freightServicesService.listPricings().catch(() => []),
        // Pricings activos del catálogo para este leg (si tiene legCatalogId).
        // Define qué proveedores cotizan este tramo y por ende, qué proveedores
        // están disponibles en el dropdown.
        leg.legCatalogId
          ? freightLegPricingsService
              .list({ legId: leg.legCatalogId, includeHistory: false })
              .catch(() => [])
          : Promise.resolve([] as FreightRouteLegProviderPricing[]),
      ]);
      setProviders(providersData as FreightProvider[]);
      setServices(servicesData);
      setServicePricings(pricingsData);
      setAllCatalogPricings(legPricingsData);
    } catch {
      // noop, individual catches above
    }
  }, [leg.legCatalogId]);

  const loadLegServices = useCallback(async () => {
    try {
      const data = await importTrackingService.getLegServices(leg.id);
      setLegServices(data);
    } catch (err) {
      console.error('Error loading leg services:', err);
    }
  }, [leg.id]);

  useEffect(() => {
    if (!open) return;
    loadCatalogs();
    loadLegServices();
    setForm({
      cargoWeightKg: asNumber(leg.cargoWeightKg),
      cargoVolumeCbm: asNumber(leg.cargoVolumeCbm),
      providerId: leg.providerId ?? '',
      pricingType: (leg.pricingType ?? 'FLAT') as FreightPricingType,
      unitPrice: asNumber(leg.unitPrice),
      minimumCharge: asNumber(leg.minimumCharge),
      currency: leg.currency || 'USD',
    });
  }, [open, leg, loadCatalogs, loadLegServices]);

  // Cargar tarifa activa del catálogo. Si el leg tiene providerId, busca la
  // tarifa de ese proveedor. Si no, toma la primera tarifa activa disponible
  // del catálogo (cualquier proveedor) y popula form.providerId con esa.
  //
  // Si el leg está huérfano (legCatalogId == null), busca en el catálogo por
  // (origin, destination, transportMode, legType). Si hay 1 match único, lo
  // sugiere como vínculo y carga sus tarifas. Al guardar, el vínculo se
  // persiste junto con el pricing.
  useEffect(() => {
    if (!open) {
      setCatalogPricing(null);
      setSuggestedVinculo(null);
      setAmbiguousCatalogCount(0);
      return;
    }
    let cancelled = false;

    const loadPricingForLeg = async (legCatalogId: string, opts: { fromSuggestion: boolean }) => {
      const filter =
        !opts.fromSuggestion && form.providerId
          ? { legId: legCatalogId, providerId: form.providerId, includeHistory: false }
          : { legId: legCatalogId, includeHistory: false };
      try {
        const data = await freightLegPricingsService.list(filter);
        if (cancelled) return;
        const pricing = data[0] ?? null;
        setCatalogPricing(pricing);

        // Si encontramos tarifa pero el leg NO tenía providerId, lo asignamos
        // automáticamente para que la próxima vez el filtro sea exacto.
        if (pricing && !form.providerId && pricing.providerId) {
          setForm((f) => ({ ...f, providerId: pricing.providerId }));
        }

        // Si el leg viene "vacío" (sin pricingType/unitPrice) y el catálogo
        // tiene tarifas, sugerimos la primera disponible. El usuario puede
        // cambiarla con los botones del panel.
        if (pricing && (leg.pricingType == null || asNumber(leg.unitPrice) === 0)) {
          const firstAvailable: FreightPricingType | null =
            pricing.ratePerKg != null
              ? 'WEIGHT'
              : pricing.ratePerCbm != null
                ? 'VOLUME'
                : pricing.flatAmount != null
                  ? 'FLAT'
                  : null;
          if (firstAvailable) {
            const rate =
              firstAvailable === 'WEIGHT'
                ? pricing.ratePerKg
                : firstAvailable === 'VOLUME'
                  ? pricing.ratePerCbm
                  : pricing.flatAmount;
            setForm((f) => ({
              ...f,
              pricingType: firstAvailable,
              unitPrice: asNumber(rate),
              minimumCharge:
                pricing.minimumCharge != null ? asNumber(pricing.minimumCharge) : f.minimumCharge,
              currency: pricing.currency || f.currency,
            }));
          }
        }
      } catch {
        if (!cancelled) setCatalogPricing(null);
      }
    };

    if (leg.legCatalogId) {
      // Camino feliz: el leg ya está vinculado.
      setSuggestedVinculo(null);
      setAmbiguousCatalogCount(0);
      loadPricingForLeg(leg.legCatalogId, { fromSuggestion: false });
    } else {
      // Leg huérfano: buscar match en el catálogo por (origin, destination,
      // transportMode, legType). El backend filtra origen/destino con
      // `contains`, así que filtramos exact match y por legType en cliente.
      freightLegCatalogService
        .list({
          origin: leg.origin,
          destination: leg.destination,
          mode: leg.transportMode,
        })
        .then((all) => {
          if (cancelled) return;
          const exact = all.filter(
            (c) =>
              c.origin === leg.origin &&
              c.destination === leg.destination &&
              c.transportMode === leg.transportMode &&
              c.legType === leg.legType &&
              c.isActive
          );
          if (exact.length === 1) {
            const match = exact[0];
            setSuggestedVinculo({
              legId: match.id,
              providerId: match.providerId ?? null,
              providerName: match.provider?.name ?? null,
            });
            setAmbiguousCatalogCount(0);
            loadPricingForLeg(match.id, { fromSuggestion: true });
          } else {
            setSuggestedVinculo(null);
            setAmbiguousCatalogCount(exact.length);
            setCatalogPricing(null);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setSuggestedVinculo(null);
            setAmbiguousCatalogCount(0);
            setCatalogPricing(null);
          }
        });
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    leg.legCatalogId,
    leg.origin,
    leg.destination,
    leg.transportMode,
    leg.legType,
    form.providerId,
  ]);

  // Aplicar una tarifa del catálogo al form (botón "elegir")
  const applyRate = (kind: 'WEIGHT' | 'VOLUME' | 'FLAT') => {
    if (!catalogPricing) return;
    const rate =
      kind === 'WEIGHT'
        ? catalogPricing.ratePerKg
        : kind === 'VOLUME'
          ? catalogPricing.ratePerCbm
          : catalogPricing.flatAmount;
    if (rate == null) return;
    setForm((f) => ({
      ...f,
      pricingType: kind,
      unitPrice: asNumber(rate),
      minimumCharge:
        catalogPricing.minimumCharge != null
          ? asNumber(catalogPricing.minimumCharge)
          : f.minimumCharge,
      currency: catalogPricing.currency || f.currency,
    }));
    toast({
      title: `Aplicado: ${KIND_LABEL[kind]}`,
      description: `${catalogPricing.currency} ${asNumber(rate).toFixed(2)}${KIND_UNIT[kind]}`,
    });
  };

  // ── Calculations preview (mirror del backend) ────────────────────
  const previewLegAmount = (() => {
    const price = form.unitPrice;
    let amt =
      form.pricingType === 'WEIGHT'
        ? price * form.cargoWeightKg
        : form.pricingType === 'VOLUME'
          ? price * form.cargoVolumeCbm
          : price;
    if (form.minimumCharge > 0 && amt < form.minimumCharge) amt = form.minimumCharge;
    return amt;
  })();

  const servicesTotal = legServices.reduce((sum, s) => sum + asNumber(s.calculatedAmount), 0);
  const totalLeg = previewLegAmount + servicesTotal;

  // ── Save leg pricing ─────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      // Si veníamos de un leg huérfano y hay sugerencia activa, persistimos el
      // vínculo al catálogo para que la próxima vez el dialog cargue tarifas
      // sin el fallback por (origin, destination, ...).
      const vinculo =
        !leg.legCatalogId && suggestedVinculo
          ? {
              legCatalogId: suggestedVinculo.legId,
              providerId: form.providerId || suggestedVinculo.providerId || null,
            }
          : {};
      await importTrackingService.updateLeg(leg.id, {
        cargoWeightKg: form.cargoWeightKg || null,
        cargoVolumeCbm: form.cargoVolumeCbm || null,
        providerId: form.providerId || null,
        pricingType: form.pricingType,
        unitPrice: form.unitPrice || null,
        minimumCharge: form.minimumCharge || null,
        currency: form.currency,
        ...vinculo,
      } as any);
      toast({ title: 'Pricing del tramo actualizado' });
      onSaved();
      onOpenChange(false);
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.message ?? 'No se pudo guardar',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Service add/remove ───────────────────────────────────────────
  const handleAddService = async (
    serviceId: string | null,
    name: string,
    pricingType: FreightPricingType,
    unitPrice: number,
    minimumCharge: number
  ) => {
    try {
      await importTrackingService.createLegService(leg.id, {
        serviceId,
        providerId: form.providerId || null,
        name,
        pricingType,
        unitPrice,
        minimumCharge: minimumCharge || null,
        currency: form.currency,
      });
      toast({ title: 'Servicio agregado' });
      await loadLegServices();
      onSaved();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.message ?? 'No se pudo agregar',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveService = async (svcLineId: string) => {
    try {
      await importTrackingService.deleteLegService(leg.id, svcLineId);
      toast({ title: 'Servicio eliminado' });
      await loadLegServices();
      onSaved();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.message ?? 'No se pudo eliminar',
        variant: 'destructive',
      });
    }
  };

  // Sugerir precio del catálogo cuando se selecciona un servicio + provider matching
  const findCatalogPricing = (serviceId: string): FreightServiceProviderPricing | undefined => {
    if (!form.providerId) return undefined;
    return servicePricings.find(
      (p) => p.serviceId === serviceId && p.providerId === form.providerId
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-primary" />
            Pricing — Tramo {leg.legOrder}: {leg.origin} → {leg.destination}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* ── Cargo ───────────────────────────────────────── */}
          <section className="space-y-3">
            <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Package className="w-4 h-4" /> Cargo del tramo
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="weight">Peso (kg)</Label>
                <Input
                  id="weight"
                  type="number"
                  step="0.01"
                  value={form.cargoWeightKg || ''}
                  onChange={(e) =>
                    setForm({ ...form, cargoWeightKg: parseFloat(e.target.value) || 0 })
                  }
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="volume">Volumen (m³)</Label>
                <Input
                  id="volume"
                  type="number"
                  step="0.0001"
                  value={form.cargoVolumeCbm || ''}
                  onChange={(e) =>
                    setForm({ ...form, cargoVolumeCbm: parseFloat(e.target.value) || 0 })
                  }
                  placeholder="0"
                />
              </div>
            </div>
          </section>

          {/* ── Lógica de cobro del tramo ──────────────────── */}
          <section className="space-y-3 border-t pt-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <Truck className="w-4 h-4" /> Lógica de cobro
              </h3>
              {leg.carrier && (
                <div className="text-xs flex items-center gap-1.5 bg-muted/50 px-2 py-1 rounded-md">
                  <span className="text-muted-foreground">Proveedor:</span>
                  <span className="font-semibold">{leg.carrier}</span>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Elegí cómo cobra el proveedor para este tramo. Las tarifas vienen de master-data →
              Catálogo de Tramos → Precios.
            </p>

            {/* Banner de vinculación cuando el leg está huérfano */}
            {!leg.legCatalogId && suggestedVinculo && (
              <div className="flex items-start gap-2 rounded-md border border-info-borde bg-info-suave p-3 text-xs">
                <Link2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold text-foreground">Tramo sin vincular al catálogo</p>
                  <p className="text-info mt-0.5">
                    Sugerencia: {leg.origin} → {leg.destination}
                    {suggestedVinculo.providerName ? ` · ${suggestedVinculo.providerName}` : ''}. Al
                    guardar el pricing, este tramo quedará vinculado automáticamente.
                  </p>
                </div>
              </div>
            )}
            {!leg.legCatalogId && !suggestedVinculo && ambiguousCatalogCount > 1 && (
              <div className="flex items-start gap-2 rounded-md border border-alerta-borde bg-alerta-suave p-3 text-xs">
                <Link2 className="w-4 h-4 text-alerta mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold text-foreground">
                    Múltiples tramos coinciden en el catálogo ({ambiguousCatalogCount})
                  </p>
                  <p className="text-alerta mt-0.5">
                    Cerrá este diálogo, editá el tramo y elegilo desde "Buscar en catálogo de
                    tramos" para fijar el proveedor correcto.
                  </p>
                </div>
              </div>
            )}
            {!leg.legCatalogId && !suggestedVinculo && ambiguousCatalogCount === 0 && (
              <div className="flex items-start gap-2 rounded-md border border-muted bg-muted/40 p-3 text-xs">
                <Link2 className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold">Sin tramo equivalente en el catálogo</p>
                  <p className="text-muted-foreground mt-0.5">
                    No encontramos un tramo del catálogo con este origen, destino y modo. Ingresá el
                    precio manualmente o creá la entrada en master-data.
                  </p>
                </div>
              </div>
            )}

            {/* Cards de selección: peso / volumen / fijo */}
            {(() => {
              // Si hay catalogPricing (proveedor del leg con tarifa cargada), sus
              // valores son la fuente. Si no, el usuario igual puede elegir una
              // lógica y se usará el unitPrice manual del leg.
              const cards = [
                {
                  kind: 'WEIGHT' as const,
                  rate: catalogPricing?.ratePerKg,
                  icon: Weight,
                  suffix: '/kg',
                  label: 'Por peso',
                },
                {
                  kind: 'VOLUME' as const,
                  rate: catalogPricing?.ratePerCbm,
                  icon: Box,
                  suffix: '/m³',
                  label: 'Por volumen',
                },
                {
                  kind: 'FLAT' as const,
                  rate: catalogPricing?.flatAmount,
                  icon: DollarSign,
                  suffix: '',
                  label: 'Monto fijo',
                },
              ];
              return (
                <div className="grid grid-cols-3 gap-2">
                  {cards.map(({ kind, rate, icon: RateIcon, suffix, label }) => {
                    const hasCatalogRate = rate != null;
                    // Si hay catalogPricing activo (el proveedor tiene tarifas
                    // cargadas), solo se pueden elegir las lógicas con tarifa.
                    // Si NO hay catalogPricing (proveedor sin precios), se
                    // permite elegir cualquiera para ingreso manual.
                    const disabled = catalogPricing != null && !hasCatalogRate;
                    const selected = form.pricingType === kind;
                    return (
                      <button
                        type="button"
                        key={kind}
                        disabled={disabled}
                        title={
                          disabled
                            ? 'El proveedor no tiene tarifa configurada para esta lógica de cobro.'
                            : undefined
                        }
                        onClick={() => {
                          if (disabled) return;
                          if (hasCatalogRate) {
                            applyRate(kind);
                          } else {
                            // Sin tarifa del catálogo: solo cambiar tipo, dejar
                            // que el usuario ingrese precio manualmente abajo.
                            setForm((f) => ({ ...f, pricingType: kind, unitPrice: 0 }));
                          }
                        }}
                        className={cn(
                          'flex flex-col items-center gap-1 py-3 px-2 rounded-md border-2 transition-colors text-center',
                          disabled
                            ? 'border-border bg-muted/40 opacity-50 cursor-not-allowed'
                            : selected
                              ? 'border-ok bg-ok-suave ring-1 ring-ok'
                              : 'border-border bg-background hover:border-ok-borde hover:bg-ok-suave cursor-pointer'
                        )}
                      >
                        <RateIcon
                          className={cn('w-5 h-5', selected ? 'text-ok' : 'text-muted-foreground')}
                        />
                        <span className="text-xs font-semibold">{label}</span>
                        {hasCatalogRate ? (
                          <span
                            className={cn(
                              'text-sm font-bold font-mono',
                              selected ? 'text-ok' : 'text-foreground'
                            )}
                          >
                            {catalogPricing!.currency} {asNumber(rate).toFixed(2)}
                            <span className="text-xs text-muted-foreground font-normal">
                              {suffix}
                            </span>
                          </span>
                        ) : disabled ? (
                          <span className="text-[10px] text-muted-foreground italic">
                            sin tarifa configurada
                          </span>
                        ) : (
                          <span className="text-[10px] text-muted-foreground italic">
                            sin tarifa · ingresá manual
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })()}

            {/* Si no hay catalogPricing para la lógica seleccionada, permitimos
                ingreso manual de precio. Si hay catálogo, el precio queda fijo */}
            {!catalogPricing && (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Precio unitario ({form.currency})</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    min="0"
                    value={form.unitPrice || ''}
                    onChange={(e) =>
                      setForm({ ...form, unitPrice: parseFloat(e.target.value) || 0 })
                    }
                  />
                </div>
                <div>
                  <Label>Cargo mínimo ({form.currency})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.minimumCharge || ''}
                    onChange={(e) =>
                      setForm({ ...form, minimumCharge: parseFloat(e.target.value) || 0 })
                    }
                    placeholder="Sin mínimo"
                  />
                </div>
                <div>
                  <Label>Moneda</Label>
                  <Input
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                    maxLength={3}
                  />
                </div>
              </div>
            )}

            {catalogPricing?.minimumCharge != null && (
              <p className="text-xs text-muted-foreground">
                Cargo mínimo aplicado del catálogo:{' '}
                <span className="font-mono font-semibold text-foreground">
                  {catalogPricing.currency} {asNumber(catalogPricing.minimumCharge).toFixed(2)}
                </span>
              </p>
            )}

            <div className="flex items-center justify-between bg-alerta-suave border border-alerta-borde rounded-md p-3 text-sm">
              <span className="text-muted-foreground">Monto transporte (preview):</span>
              <span className="font-bold font-mono">
                {formatMoney(previewLegAmount, form.currency)}
              </span>
            </div>
          </section>

          {/* ── Servicios extra del tramo ──────────────────── */}
          <section className="space-y-3 border-t pt-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                Servicios del tramo ({legServices.length})
              </h3>
            </div>

            {legServices.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">
                Sin servicios extra. Agregá trámites, mano de obra, etc.
              </p>
            ) : (
              <div className="space-y-1.5">
                {legServices.map((svc) => (
                  <div
                    key={svc.id}
                    className="flex items-center gap-2 p-2 rounded-md border bg-muted/30"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{svc.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {FREIGHT_PRICING_LABEL[svc.pricingType]} · {svc.currency}{' '}
                        {asNumber(svc.unitPrice).toFixed(4)}
                      </div>
                    </div>
                    <div className="text-sm font-mono font-bold whitespace-nowrap">
                      {formatMoney(svc.calculatedAmount, svc.currency)}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveService(svc.id)}
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <AddServiceForm
              services={services}
              onAdd={handleAddService}
              findCatalogPricing={findCatalogPricing}
            />
          </section>

          {/* ── Total ──────────────────────────────────────── */}
          <section className="border-t pt-4">
            <div className="flex items-center justify-between bg-ok-suave border border-ok-borde rounded-md p-3">
              <span className="text-sm font-bold text-foreground">
                Total tramo (transporte + servicios):
              </span>
              <span className="text-lg font-bold font-mono text-ok">
                {formatMoney(totalLeg, form.currency)}
              </span>
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar pricing'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Sub-componente: form de agregar servicio ────────────────────────
function AddServiceForm({
  services,
  onAdd,
  findCatalogPricing,
}: {
  services: FreightService[];
  onAdd: (
    serviceId: string | null,
    name: string,
    pricingType: FreightPricingType,
    unitPrice: number,
    minimumCharge: number
  ) => void;
  findCatalogPricing: (serviceId: string) => FreightServiceProviderPricing | undefined;
}) {
  const [serviceId, setServiceId] = useState<string>('');
  const [name, setName] = useState('');
  const [pricingType, setPricingType] = useState<FreightPricingType>('FLAT');
  const [unitPrice, setUnitPrice] = useState(0);
  const [minimumCharge, setMinimumCharge] = useState(0);

  const handleSelectService = (sid: string) => {
    setServiceId(sid);
    const svc = services.find((s) => s.id === sid);
    if (svc) {
      setName(svc.name);
      const cat = findCatalogPricing(sid);
      if (cat) {
        setPricingType(cat.pricingType);
        setUnitPrice(asNumber(cat.unitPrice));
        setMinimumCharge(asNumber(cat.minimumCharge));
      }
    }
  };

  const handleSubmit = () => {
    if (!name) return;
    onAdd(serviceId || null, name, pricingType, unitPrice, minimumCharge);
    setServiceId('');
    setName('');
    setPricingType('FLAT');
    setUnitPrice(0);
    setMinimumCharge(0);
  };

  return (
    <div className="border border-dashed rounded-md p-3 space-y-2">
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-4">
          <Select
            value={serviceId || '__custom__'}
            onValueChange={(v) => v !== '__custom__' && handleSelectService(v)}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Catálogo o ad-hoc" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__custom__">— Servicio ad-hoc —</SelectItem>
              {services.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Input
          className="col-span-3 h-8 text-xs"
          placeholder="Nombre"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Select value={pricingType} onValueChange={(v) => setPricingType(v as FreightPricingType)}>
          <SelectTrigger className="col-span-2 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRICING_TYPES.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          className="col-span-2 h-8 text-xs"
          type="number"
          step="0.0001"
          placeholder="Precio"
          value={unitPrice || ''}
          onChange={(e) => setUnitPrice(parseFloat(e.target.value) || 0)}
        />
        <Button
          type="button"
          size="sm"
          onClick={handleSubmit}
          disabled={!name}
          className="col-span-1 h-8 px-2"
        >
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}
